import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { GLOSSARY, GLOSSARY_CATEGORIES } from "../data/glossary";
import { SERVICES } from "../data/services";
import { Seo } from "../components/Seo";
import { MaskedLine, Reveal } from "../components/Reveal";
import { CTABanner } from "../components/sections/CTABanner";
import { SITE_URL } from "../data/site";

const TERM_TO_SERVICE = {
  // Managed IT Services
  "MSP": "managed-it-services", "RMM": "managed-it-services", "PSA": "managed-it-services",
  "vCIO": "managed-it-services", "QBR": "managed-it-services", "TAM": "managed-it-services",
  "Break/Fix": "managed-it-services", "TCO": "managed-it-services", "Co-Managed IT": "managed-it-services",
  "Onboarding": "managed-it-services", "LAN / WAN": "managed-it-services", "SD-WAN": "managed-it-services",
  "NAS": "managed-it-services", "RAID": "managed-it-services", "UPS": "managed-it-services",
  "VoIP": "managed-it-services", "ISP": "managed-it-services",
  // Cybersecurity
  "MSSP": "cybersecurity", "EDR": "cybersecurity", "MDR": "cybersecurity", "XDR": "cybersecurity",
  "ITDR": "cybersecurity", "SIEM": "cybersecurity", "SOC": "cybersecurity", "MFA": "cybersecurity",
  "2FA": "cybersecurity", "SSO": "cybersecurity", "IAM": "cybersecurity", "Zero Trust": "cybersecurity",
  "ZTNA": "cybersecurity", "BEC": "cybersecurity", "Phishing": "cybersecurity", "CVE": "cybersecurity",
  "CVSS": "cybersecurity", "Zero-Day": "cybersecurity", "NGAV": "cybersecurity", "DLP": "cybersecurity",
  "MDM": "cybersecurity", "CASB": "cybersecurity", "PKI": "cybersecurity", "TLS / SSL": "cybersecurity",
  "vCISO": "cybersecurity", "HIPAA": "cybersecurity", "PCI DSS": "cybersecurity", "NIST CSF": "cybersecurity",
  "CMMC": "cybersecurity", "AUP": "cybersecurity",
  // Cloud & Microsoft 365
  "SaaS": "cloud-microsoft-365", "IaaS": "cloud-microsoft-365", "PaaS": "cloud-microsoft-365",
  "M365": "cloud-microsoft-365", "Entra ID": "cloud-microsoft-365", "Tenant": "cloud-microsoft-365",
  "CA Policy": "cloud-microsoft-365", "EULA": "cloud-microsoft-365",
  // Help Desk
  "SLA": "help-desk-support",
  // Backup & DR
  "BDR": "backup-disaster-recovery", "RPO": "backup-disaster-recovery", "RTO": "backup-disaster-recovery",
  "Immutable Backup": "backup-disaster-recovery", "Object Lock": "backup-disaster-recovery",
  "BaaS": "backup-disaster-recovery", "DRaaS": "backup-disaster-recovery", "BCP": "backup-disaster-recovery",
};

export default function Glossary() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return GLOSSARY;
    return GLOSSARY.filter(
      (g) => g.term.toLowerCase().includes(q) || g.full.toLowerCase().includes(q) || g.def.toLowerCase().includes(q)
    );
  }, [query]);

  const definedTermSetLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: "Sempresera IT & MSP Glossary",
    url: `${SITE_URL}/glossary`,
    hasDefinedTerm: GLOSSARY.map((g) => ({
      "@type": "DefinedTerm",
      name: g.full,
      alternateName: g.term,
      description: g.def,
    })),
  };

  return (
    <>
      <Seo
        title="IT Glossary: 60+ MSP & Cybersecurity Acronyms Explained | Sempresera"
        description="MSP, EDR, MDR, SIEM, SOC, RTO, RPO and 55+ more IT acronyms explained in plain English — the glossary to keep open while comparing IT service providers."
        path="/glossary"
        jsonLd={[definedTermSetLd]}
      />

      <section className="mx-auto max-w-7xl px-5 pb-24 pt-36 md:px-8 md:pt-44" data-testid="glossary-page">
        <MaskedLine delay={0.1}>
          <span className="eyebrow">Glossary</span>
        </MaskedLine>
        <h1 className="mt-6 max-w-3xl font-display text-4xl font-black leading-[1.02] tracking-tighter md:text-6xl">
          <MaskedLine delay={0.25}>
            <span className="text-metallic">Every IT acronym,</span>
          </MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-metallic">in plain English.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.55}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-[#C9D2DE]">
            Shopping for an IT provider means drowning in alphabet soup — EDR, SIEM, RPO, vCIO. Keep this page open
            in the next tab while you compare proposals.
          </p>
        </MaskedLine>

        <Reveal delay={0.6}>
          <div className="relative mt-12 max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8B4C2]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search an acronym — try EDR, SOC, RTO…"
              className="field !pl-11"
              data-testid="glossary-search"
              aria-label="Search glossary terms"
            />
          </div>
          <p className="mt-3 text-xs text-zinc-600" data-testid="glossary-count">
            {filtered.length} of {GLOSSARY.length} terms
          </p>
        </Reveal>

        {GLOSSARY_CATEGORIES.map((cat) => {
          const terms = filtered.filter((g) => g.cat === cat.id);
          if (terms.length === 0) return null;
          return (
            <div key={cat.id} className="mt-20" data-testid={`glossary-cat-${cat.id}`}>
              <Reveal>
                <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{cat.label}</h2>
              </Reveal>
              <div className="mt-8 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-2">
                {terms.map((g) => {
                  const service = SERVICES.find((s) => s.slug === TERM_TO_SERVICE[g.term]);
                  return (
                    <div key={g.term} className="bg-ink p-6" data-testid={`glossary-term-${g.term.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      <div className="flex flex-wrap items-baseline gap-x-3">
                        <span className="font-display text-xl font-black tracking-tight text-metallic">{g.term}</span>
                        <span className="text-sm font-medium text-zinc-300">{g.full}</span>
                      </div>
                      <p className="mt-3 text-sm leading-relaxed text-[#C9D2DE]">{g.def}</p>
                      {service && (
                        <Link
                          to={`/services/${service.slug}`}
                          className="group mt-4 inline-flex items-center gap-1.5 font-display text-xs font-bold uppercase tracking-[0.15em] text-brand/80 transition-colors duration-200 hover:text-brand"
                          data-testid={`glossary-link-${g.term.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        >
                          {service.name} <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <p className="glass mt-12 p-12 text-sm text-[#A8B4C2]" data-testid="glossary-empty">
            No terms match "{query}". Try a shorter search.
          </p>
        )}
      </section>

      <CTABanner
        title="Prefer a translator on staff?"
        sub="Book a free consultation — we'll review any provider's proposal with you and decode every acronym in it, even if you don't choose us."
      />
    </>
  );
}
